"use client";

/**
 * Sticky marketing nav for the pre-launch landing page.
 *
 * Design notes (from the Claude Design handoff):
 *  - Background is FULLY OPAQUE (`bg-[color:var(--paper)]`), not the
 *    usual translucent `bg-background/80 backdrop-blur`. The page has
 *    a dark "stakes band" section, and a translucent nav lets the
 *    dark section bleed through, killing logo contrast. Opaque is
 *    the simpler, more robust fix than swapping logos by scroll
 *    position.
 *  - Nav is `h-14`, the SVG logo is `h-7` so it has visual weight
 *    without dominating the bar.
 *  - Links use the `.ulink` underline-on-hover treatment defined in
 *    globals.css. Hover transitions ink-2 → ink.
 *  - Public launch CTAs: a secondary "Book a workflow review"
 *    (→ /book/product-fit Cal redirect), a low-emphasis "Log in"
 *    (→ /login on the app subdomain), and the primary "Start free
 *    trial" in solid ink (→ the #pricing section).
 */

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { loginUrl } from "@/lib/urls/app-links";
import { TrackedLink } from "@/components/analytics/tracked-link";

// Absolute landing-page anchors (`/#…`, not bare `#…`). A bare hash resolves
// against the CURRENT path, so on a guide/comparison page "Pricing" would just
// append `#pricing` to that URL and do nothing. `/#pricing` always routes to the
// landing page's pricing section (and still scrolls in place when already on /).
// Matches the header CTAs and the footer links.
const NAV_LINKS = [
  { label: "How it works", href: "/#how" },
  { label: "Components", href: "/#components" },
  { label: "Features", href: "/#features" },
  // Two restrained content hubs (not mega-menus): Solutions (by platform/role)
  // and Learn (guides, comparisons, references). Each routes to its index page.
  { label: "Solutions", href: "/solutions" },
  { label: "Learn", href: "/learn" },
  { label: "Pricing", href: "/#pricing" },
  { label: "FAQ", href: "/#faq" },
];

export function MarketingNav() {
  const [open, setOpen] = React.useState(false);

  return (
    <header className="sticky top-0 z-30 bg-[color:var(--paper)] border-b rule">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8 h-14 flex items-center justify-between">
        {/* Brand mark */}
        <Link href="/" className="flex items-center group" aria-label="PCNshark">
          <Image
            src="/brand/pcnshark-logo.svg"
            alt="PCNshark"
            // Width/height come from the SVG's intrinsic aspect ratio
            // (~3.7:1 — wordmark + mark). Setting the height with
            // Tailwind and letting `width: auto` win on the inline
            // style keeps the proportions intact across viewports.
            width={140}
            height={28}
            priority
            className="h-7 w-auto"
          />
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-7 text-[13px] ink-2">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className="ulink hover:text-[color:var(--ink)]"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTAs. Public launch: a secondary "Book a workflow
            review" (→ /book/product-fit Cal redirect, new tab), the
            re-exposed "Log in", and the primary "Start free trial"
            (→ the pricing section, where the Starter/Team-only trial is
            made clear). */}
        <div className="hidden sm:flex items-center gap-2">
          <TrackedLink
            href="/book/product-fit"
            event="product_fit_booking_clicked"
            eventProps={{ cta_location: "header" }}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-[13px] font-medium border rule hover:border-[color:var(--ink)] transition"
          >
            Book a workflow review
          </TrackedLink>
          <Link
            href={loginUrl()}
            className="px-3 py-1.5 text-[13px] font-medium ink-2 hover:text-[color:var(--ink)] transition"
          >
            Log in
          </Link>
          <TrackedLink
            href="/#pricing"
            event="header_trial_cta_clicked"
            eventProps={{ cta_location: "header" }}
            className="px-3 py-1.5 bg-[color:var(--ink)] text-white text-[13px] font-medium hover:bg-black transition"
          >
            Start free trial
          </TrackedLink>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center sm:hidden text-[color:var(--ink)]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle navigation menu"
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={cn(
          "border-t rule sm:hidden bg-[color:var(--paper)]",
          open ? "block" : "hidden",
        )}
      >
        <div className="flex flex-col px-6 py-4 gap-3">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className="text-[14px] ink-2 hover:text-[color:var(--ink)]"
              onClick={() => setOpen(false)}
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-2 mt-2 border-t rule flex flex-col gap-2">
            <Link
              href="/book/product-fit"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="px-3 py-2 text-center text-[13px] font-medium border rule hover:border-[color:var(--ink)] transition"
            >
              Book a workflow review
            </Link>
            <Link
              href={loginUrl()}
              onClick={() => setOpen(false)}
              className="px-3 py-2 text-center text-[13px] font-medium border rule hover:border-[color:var(--ink)] transition"
            >
              Log in
            </Link>
            <Link
              href="/#pricing"
              onClick={() => setOpen(false)}
              className="px-3 py-2 text-center bg-[color:var(--ink)] text-white text-[13px] font-medium hover:bg-black transition"
            >
              Start free trial
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
